import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */
/* @jsx mdx */
import DefaultLayout from "/home/runner/work/design/design/node_modules/@primer/gatsby-theme-doctocat/src/components/layout.js";
import ComponentLayout from '~/src/layouts/component-layout';
import { AccessibilityLink } from '~/src/components/accessibility-link';
export const _frontmatter = {};
const makeShortcode = name => function MDXDefaultShortcode(props) {
  console.warn("Component " + name + " was not imported, exported, or provided by MDXProvider as global scope");
  return <div {...props} />;
};
const Box = makeShortcode("Box");
const layoutProps = {
  _frontmatter
};
const MDXLayout = ComponentLayout;
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">





    <h2>{`Usage`}</h2>
    <p>{`The underline nav is used to provide navigation links to related views for the user's current context. It is typically used as primary navigation at the top of the page or as secondary or tertiary navigation when nested in views with a `}<a parentName="p" {...{
        "href": "/components/nav-list"
      }}>{`nav list`}</a>{`.`}</p>
    <h2>{`Anatomy`}</h2>
    <p><img parentName="p" {...{
        "src": "https://user-images.githubusercontent.com/175638/186432408-c2479dd2-31fe-4261-9f9a-e67d49b28e72.png",
        "alt": "Anatomy of the UnderlineNav component"
      }}></img></p>
    <ul>
      <li parentName="ul"><strong parentName="li">{`Item`}</strong>{`: A navigation link.`}</li>
      <li parentName="ul"><strong parentName="li">{`Current item`}</strong>{`: The item currently selected is highlighted with an underline.`}</li>
      <li parentName="ul"><strong parentName="li">{`Label`}</strong>{`: Describes the navigation item.`}</li>
      <li parentName="ul"><strong parentName="li">{`Leading icon (optional)`}</strong>{`: An icon that identifies the navigation item.`}</li>
      <li parentName="ul"><strong parentName="li">{`Counter (optional)`}</strong>{`: Displays a count next to the label.`}</li>
      <li parentName="ul"><strong parentName="li">{`Overflow menu`}</strong>{`: A disclosure menu that displays additional items when there are too many to fit in the available space.`}</li>
    </ul>
    <h2>{`Options`}</h2>
    <h3>{`Item`}</h3>
    <p>{`The underline nav is composed of a list of items. Each item is a link that can be selected to navigate to a different page in the current context.`}</p>
    <h4>{`Label text`}</h4>
    <p>{`The title of the view associated with that tab. Keep labels clear, short, and concise.`}</p>
    <h4>{`Leading icons`}</h4>
    <p>{`Use an `}<a parentName="p" {...{
        "href": "/foundations/icons"
      }}>{`Octicon`}</a>{` to improve the scannability of common items, but only when relevant. When adding leading icons, all items in the navigation should have one.`}</p>
    <p>{`Make sure that icons used are consistent across views, and that they are not repeated for different navigations across the product.`}</p>
    <h4>{`Counters`}</h4>
    <p>{`You may include a counter to indicate the number of related resources contained in the desitination page, such as the number of open issues.`}</p>
    <p>{`When loading multiple counters asynchronously, wait for all the data to be ready before displaying the counters, so you can avoid multiple layout shifts. Use the counter loading state while waiting for the data.`}</p>
    <h2>{`Layout`}</h2>
    <h3>{`Placement`}</h3>
    <p>{`The underline nav has two possible main placements depending on its intended usage:`}</p>
    <ul>
      <li parentName="ul">{`As primary navigation: place it at the top of the page. Ensure any secondary and tertiary navigation is nested under the underline nav.`}</li>
      <li parentName="ul">{`As secondary or tertiary navigation: place it above the main content of the page.`}</li>
    </ul>
    <p>{`In either scenarios, avoid stacking multiple uderline navs to display multiple levels of navigation.`}</p>
    <h3>{`Alignment`}</h3>
    <p>{`Align the the underline nav to the left and, when possible, make the component span the available width.`}</p>
    <h3>{`Responsive design`}</h3>
    <p><img parentName="p" {...{
        "src": "https://user-images.githubusercontent.com/175638/229456836-bfa1ddbb-5cef-4dbc-a0db-da77814c63a2.png",
        "alt": "The underline nav without overflowing items and the component on a smaller viewport showing the overflow menu"
      }}></img></p>
    <p>{`Underline nav handles overflowing items automatically by collapsing them into a disclosure menu. When some of the items don't fit the available space, the component will first hide leading icons to economize space and then move items to the overflow menu one by one.`}</p>
    <h2>{`Accessibility`}</h2>
    <p>{`Set `}<inlineCode parentName="p">{`aria-current`}</inlineCode>{` to `}<inlineCode parentName="p">{`"page"`}</inlineCode>{` to indicate that the item represents the current page. Set `}<inlineCode parentName="p">{`aria-current`}</inlineCode>{` to `}<inlineCode parentName="p">{`"location"`}</inlineCode>{` to indicate that the item represents the current location on a page.`}</p>
    <h3>{`Keyboard navigation`}</h3>
    <p>{`Items can be activated using a cursor or keyboard`}</p>
    <table>
      <thead parentName="table">
        <tr parentName="thead">
          <th parentName="tr" {...{
            "align": null
          }}>{`Key(s)`}</th>
          <th parentName="tr" {...{
            "align": null
          }}>{`Description`}</th>
        </tr>
      </thead>
      <tbody parentName="table">
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><Box as="kbd" backgroundColor="canvas.subtle" borderRadius={1} borderColor="border.default" borderWidth={1} borderStyle="solid" py={1} px={2} mdxType="Box">{`Enter`}</Box></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Activates the focused item.`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><Box as="kbd" backgroundColor="canvas.subtle" borderRadius={1} borderColor="border.default" borderWidth={1} borderStyle="solid" py={1} px={2} mdxType="Box">{`Tab`}</Box></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Moves focus to the next item.`}</td>
        </tr>
      </tbody>
    </table>
    <h3>{`Known accessibility issues (GitHub staff only)`}</h3>
 <AccessibilityLink label="UnderlineNav" mdxType="AccessibilityLink" />
    <h2>{`Related links`}</h2>
    <ul>
      <li parentName="ul"><a parentName="li" {...{
          "href": "/components/nav-list"
        }}>{`Nav list`}</a></li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "/components/tab-nav"
        }}>{`Tab nav`}</a></li>
    </ul>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      